import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import API_URL, {
  equipmentDrawingsListPath,
  equipmentDrawingDeletePath,
  equipmentDrawingRenamePath,
  equipmentHoldHistoryPath,
} from "../Config";
import axiosInstance from "../axiosinstance.js";
import { FaChevronDown } from "react-icons/fa6";
import { Tooltip, CircularProgress, Drawer, IconButton } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from "@mui/material";
import { IoArrowBack } from "react-icons/io5";
import { LuFileText } from "react-icons/lu";
import {
  CloudUpload as CloudUploadIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  PauseCircleOutline as HoldIcon,
  HistoryRounded as HistoryIcon,
  CloseRounded as CloseIcon,
  PlayCircleOutline as ReleaseIcon,
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import ProcessFlowchartVisualization from "./ProcessFlowchartVisualization";
import EquipmentExecutionTable from "./EquipmentExecutionTable";
import EquipmentDrawingUploadModal, {
  canManageEquipmentDrawings,
} from "../Components/EquipmentDrawingUploadModal.js";
import DrawingPreviewModal from "../Components/DrawingPreviewModal.js";
import EquipmentHoldControl from "../Components/EquipmentHoldControl.js";
import { formatDrawingMeta } from "../utils/drawingMeta.js";
import "../Components/jeconDialog.css";
import "./EquipmentDetails.css";

const preserveLocalBypassMetadata = (previousSteps, nextSteps) =>
  nextSteps.map((nextStep) => {
    // Once the list endpoint includes this field, its value is authoritative.
    if (Object.prototype.hasOwnProperty.call(nextStep, "is_bypassed")) {
      return nextStep;
    }

    const previousStep = previousSteps.find(
      (step) => String(step?.process_id) === String(nextStep?.process_id),
    );
    if (!previousStep?.is_bypassed) return nextStep;

    return {
      ...nextStep,
      is_bypassed: true,
      bypassed_by: previousStep.bypassed_by ?? null,
      bypassed_by_name: previousStep.bypassed_by_name ?? null,
      bypassed_at: previousStep.bypassed_at ?? null,
      bypass_reason: previousStep.bypass_reason ?? null,
    };
  });

const EquipmentDetails = ({ isSidebarExpanded }) => {
  const { equipment_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [equipmentInfo, setEquipmentInfo] = useState(null);
  const [processSteps, setProcessSteps] = useState([]);
  const [tableProcessSteps, setTableProcessSteps] = useState([]);
  const [dependencyMap, setDependencyMap] = useState({});
  const [tableDependencyMap, setTableDependencyMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFlowchartExpanded, setIsFlowchartExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [actionableOnly] = useState(false);
  const [highlightedProcessId, setHighlightedProcessId] = useState(null);
  const hasAutoFocusedProcessRef = useRef(false);
  const highlightTimeoutRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const processUpdateRefetchTimerRef = useRef(null);
  const searchDebounceTimerRef = useRef(null);
  const processStepsRef = useRef([]);
  const stickyHeaderRef = useRef(null);
  const lastFetchKeyRef = useRef("");
  const currentSearchQueryRef = useRef("");
  const [realtimeProcessUpdate, setRealtimeProcessUpdate] = useState(null);
  const [realtimeUpdateSignal, setRealtimeUpdateSignal] = useState(0);
  const [dependencyRedirectOpen, setDependencyRedirectOpen] = useState(false);
  const [pendingDependencyRedirect, setPendingDependencyRedirect] = useState(null);
  const [isStickyHeaderElevated, setIsStickyHeaderElevated] = useState(false);
  const [drawingUploadOpen, setDrawingUploadOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [drawingsCount, setDrawingsCount] = useState(0);
  const [drawingsListOpen, setDrawingsListOpen] = useState(false);
  const [drawingsList, setDrawingsList] = useState([]);
  const [drawingsListLoading, setDrawingsListLoading] = useState(false);
  const [drawingDeleteTarget, setDrawingDeleteTarget] = useState(null);
  const [drawingDeleteBusy, setDrawingDeleteBusy] = useState(false);
  const [drawingRenameTarget, setDrawingRenameTarget] = useState(null);
  const [drawingRenameValue, setDrawingRenameValue] = useState("");
  const [drawingRenameBusy, setDrawingRenameBusy] = useState(false);
  const [holdHistoryOpen, setHoldHistoryOpen] = useState(false);
  const [holdHistory, setHoldHistory] = useState([]);
  const [holdHistoryLoading, setHoldHistoryLoading] = useState(false);
  const [holdHistoryError, setHoldHistoryError] = useState("");
  const canManageDrawings = canManageEquipmentDrawings();
  const roleName = (localStorage.getItem("role_name") || "")
    .trim()
    .toLowerCase();
  // Prefer the backend capability when available. The role fallback supports
  // older detail responses; the backend remains the authoritative permission
  // check for every mutation.
  const roleCanManageHold = [
    "admin",
    "administrator",
    "site admin",
    "super admin",
    "planner",
    "planning",
    "field technician",
  ].includes(roleName);

  const incomingProcessId = useMemo(() => {
    const stateProcessId = location?.state?.process_id ?? location?.state?.processId;
    if (stateProcessId) return String(stateProcessId);

    const search = new URLSearchParams(location?.search || "");
    return search.get("process_id") || search.get("processId") || "";
  }, [location?.state, location?.search]);

  useEffect(() => {
    hasAutoFocusedProcessRef.current = false;
  }, [equipment_id, incomingProcessId]);

  useEffect(() => {
    processStepsRef.current = processSteps;
  }, [processSteps]);
  useEffect(() => {
    currentSearchQueryRef.current = debouncedSearchQuery;
  }, [debouncedSearchQuery]);
  const addAdminNoteToStep = (processId, note) => {
    const appendNote = (steps) =>
      steps.map((step) =>
        step.process_id === processId
          ? {
              ...step,
              admin_notes: [...(step.admin_notes || []), { content: note }],
            }
          : step,
      );

    setProcessSteps((prev) => appendNote(prev));
    setTableProcessSteps((prev) => appendNote(prev));
  };

  const applyBypassToProcess = useCallback((bypassedProcess) => {
    const processId = String(bypassedProcess?.process_id || "");
    if (!processId) return;

    const applyBypass = (steps) =>
      steps.map((step) =>
        String(step?.process_id) === processId
          ? {
              ...step,
              is_bypassed: true,
              bypassed_by: bypassedProcess.bypassed_by ?? null,
              bypassed_by_name: bypassedProcess.bypassed_by_name ?? null,
              bypassed_at: bypassedProcess.bypassed_at ?? null,
              bypass_reason: bypassedProcess.bypass_reason ?? null,
            }
          : step,
      );

    setProcessSteps((previous) => applyBypass(previous));
    setTableProcessSteps((previous) => applyBypass(previous));
  }, []);

  const fetchEquipmentDetails = useCallback(
    async ({
      search = currentSearchQueryRef.current,
      showFullPageLoader = false,
      force = false,
      silent = false,
    } = {}) => {
      if (!equipment_id) return;

      const normalizedSearch = String(search || "").trim();
      const requestKey = JSON.stringify({
        equipmentId: String(equipment_id),
        search: normalizedSearch,
        actionableOnly,
      });

      // `force` bypasses the dedupe guard so a refresh triggered after a
      // mutation (Start/End) always refetches, even though the requestKey is
      // unchanged. Without this the table keeps stale rows and the newly saved
      // actual_start/actual_end/duration_analysis never appear.
      if (lastFetchKeyRef.current === requestKey && !showFullPageLoader && !force) {
        return;
      }

      lastFetchKeyRef.current = requestKey;

      if (showFullPageLoader) {
        setLoading(true);
      } else if (!silent) {
        setTableLoading(true);
      }
      setError(null);

      try {
        const params = new URLSearchParams();
        params.set("actionable_only", actionableOnly ? "true" : "false");
        if (normalizedSearch) {
          params.set("search", normalizedSearch);
        }

        const url = `${API_URL}/api/get_processes_for_equipment/${equipment_id}?${params.toString()}`;
        const res = await axiosInstance.get(url);
        const data = res.data;
        console.log("[TEMP][get_processes_for_equipment] raw response:", data);

        const nextProcessSteps = data.process_steps || [];
        const responseEquipmentInfo = data.equipment_info || null;
        // Keep compatibility with deployments that expose hold metadata at the
        // response root while treating equipment_info as the canonical shape.
        const holdInfo = {
          is_on_hold:
            responseEquipmentInfo?.is_on_hold ?? data.is_on_hold ?? false,
          hold_reason:
            responseEquipmentInfo?.hold_reason ?? data.hold_reason ?? null,
          held_at: responseEquipmentInfo?.held_at ?? data.held_at ?? null,
          held_by: responseEquipmentInfo?.held_by ?? data.held_by ?? null,
          can_manage_hold:
            responseEquipmentInfo?.can_manage_hold ??
            data.can_manage_hold,
        };
        setEquipmentInfo((prev) => {
          if (!responseEquipmentInfo && !prev) return null;
          return {
            ...(prev || {}),
            ...(responseEquipmentInfo || {}),
            ...holdInfo,
          };
        });

        const transformedDependencyMap = {};
        if (Array.isArray(data.dependency_map)) {
          data.dependency_map.forEach((dep) => {
            transformedDependencyMap[dep.process_code] = {
              predecessors: dep.predecessors || [],
              relation: dep.relation || "FS",
            };
          });
        }
        if (showFullPageLoader || !normalizedSearch) {
          setProcessSteps((previous) =>
            preserveLocalBypassMetadata(previous, nextProcessSteps),
          );
          setDependencyMap(transformedDependencyMap);
        }
        setTableProcessSteps((previous) =>
          preserveLocalBypassMetadata(previous, nextProcessSteps),
        );
        setTableDependencyMap(transformedDependencyMap);
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to fetch equipment details";
        setError(errorMessage);
        console.error("Error fetching equipment details:", err);
      } finally {
        if (showFullPageLoader) {
          setLoading(false);
        } else if (!silent) {
          setTableLoading(false);
        }
      }
    },
    [actionableOnly, equipment_id],
  );

  useEffect(() => {
    if (!equipment_id) return;

    setEquipmentInfo(null);
    setProcessSteps([]);
    setTableProcessSteps([]);
    setDependencyMap({});
    setTableDependencyMap({});
    setSearchQuery("");
    setDebouncedSearchQuery("");
    lastFetchKeyRef.current = "";
    fetchEquipmentDetails({ search: "", showFullPageLoader: true });
  }, [equipment_id, fetchEquipmentDetails]);

  useEffect(() => {
    const handleHoldUpdate = (event) => {
      const updatedEquipmentId = String(event?.detail?.equipment_id || "");
      if (!updatedEquipmentId || updatedEquipmentId !== String(equipment_id)) {
        return;
      }

      // Always refetch instead of trusting the push payload: the detail API is
      // authoritative for permissions, holder metadata, and process gates.
      fetchEquipmentDetails({ force: true, silent: true });
    };

    window.addEventListener("equipment-hold-updated", handleHoldUpdate);
    return () => {
      window.removeEventListener("equipment-hold-updated", handleHoldUpdate);
    };
  }, [equipment_id, fetchEquipmentDetails]);

  useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 400);

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    if (!equipment_id) return;
    if (!equipmentInfo) return;

    fetchEquipmentDetails({ search: debouncedSearchQuery, showFullPageLoader: false });
  }, [debouncedSearchQuery, actionableOnly, equipment_id, equipmentInfo, fetchEquipmentDetails]);

  useEffect(() => {
    const handleRealtimeProcessUpdate = (event) => {
      const payload = event?.detail || {};
      const payloadEquipmentId = String(payload?.equipment_id || "");
      const currentEquipmentId = String(equipment_id || "");

      if (!payloadEquipmentId || payloadEquipmentId !== currentEquipmentId) return;

      const processId = String(payload?.process_id || "");
      if (!processId) return;

      const hasMatchingProcess = processStepsRef.current.some(
        (step) => String(step?.process_id) === processId,
      );
      if (!hasMatchingProcess) return;

      const action = String(payload?.action || "").trim();
      const isBypassUpdate = action.toLowerCase() === "bypass";
      const statusFromPayload = payload?.status;
      const fallbackStatus =
        action.toLowerCase() === "start"
          ? "In Progress"
          : action.toLowerCase() === "end"
            ? "Completed"
            : null;
      const resolvedStatus = isBypassUpdate
        ? "Bypassed"
        : statusFromPayload || fallbackStatus;

      setProcessSteps((prev) =>
        prev.map((step) =>
          String(step?.process_id) === processId
            ? {
                ...step,
                ...(isBypassUpdate
                  ? { is_bypassed: true }
                  : resolvedStatus
                    ? { status: resolvedStatus }
                    : {}),
              }
            : step,
        ),
      );
      setTableProcessSteps((prev) =>
        prev.map((step) =>
          String(step?.process_id) === processId
            ? {
                ...step,
                ...(isBypassUpdate
                  ? { is_bypassed: true }
                  : resolvedStatus
                    ? { status: resolvedStatus }
                    : {}),
              }
            : step,
        ),
      );

      setRealtimeProcessUpdate({
        equipment_id: payloadEquipmentId,
        process_id: processId,
        status: resolvedStatus,
        action,
        received_at: Date.now(),
      });
      setRealtimeUpdateSignal((prev) => prev + 1);

      if (processUpdateRefetchTimerRef.current) {
        clearTimeout(processUpdateRefetchTimerRef.current);
      }

      processUpdateRefetchTimerRef.current = setTimeout(() => {
        fetchEquipmentDetails({
          search: currentSearchQueryRef.current,
          showFullPageLoader: false,
        });
      }, 900);
    };

    window.addEventListener("process-update-received", handleRealtimeProcessUpdate);
    return () => {
      window.removeEventListener("process-update-received", handleRealtimeProcessUpdate);
    };
  }, [equipment_id]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (processUpdateRefetchTimerRef.current) {
        clearTimeout(processUpdateRefetchTimerRef.current);
      }
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleStickyHeaderShadow = () => {
      const headerElement = stickyHeaderRef.current;
      if (!headerElement) return;

      setIsStickyHeaderElevated(headerElement.getBoundingClientRect().top <= 0);
    };

    handleStickyHeaderShadow();
    window.addEventListener("scroll", handleStickyHeaderShadow, { passive: true });
    window.addEventListener("resize", handleStickyHeaderShadow);

    return () => {
      window.removeEventListener("scroll", handleStickyHeaderShadow);
      window.removeEventListener("resize", handleStickyHeaderShadow);
    };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      // Format as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (error) {
      return "N/A";
    }
  };

  const formatHoldDateTime = (dateString) => {
    if (!dateString) return "Not available";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "Not available";

    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
      .format(date)
      .replace(/\b(am|pm)\b/i, (period) => period.toUpperCase());
  };

  const formatHoldDuration = (dateString) => {
    if (!dateString) return "Not available";
    const heldDate = new Date(dateString);
    if (Number.isNaN(heldDate.getTime())) return "Not available";

    const elapsedMinutes = Math.max(
      0,
      Math.floor((Date.now() - heldDate.getTime()) / 60000),
    );
    const days = Math.floor(elapsedMinutes / 1440);
    const hours = Math.floor((elapsedMinutes % 1440) / 60);
    const minutes = elapsedMinutes % 60;

    if (days) return `${days}d ${hours}h`;
    if (hours) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const openHoldHistory = async () => {
    const id = equipmentInfo?.id || equipment_id;
    if (!id) return;

    setHoldHistoryOpen(true);
    setHoldHistoryLoading(true);
    setHoldHistoryError("");

    try {
      const response = await axiosInstance.get(
        `${API_URL}${equipmentHoldHistoryPath(id)}`,
      );
      setHoldHistory(Array.isArray(response?.data?.data) ? response.data.data : []);
    } catch (requestError) {
      setHoldHistoryError(
        requestError?.response?.data?.message ||
          "Unable to load the equipment hold history.",
      );
    } finally {
      setHoldHistoryLoading(false);
    }
  };

  // const getEquipmentStatus = () => {
  //   if (!equipmentInfo?.start_eta || !equipmentInfo?.finish_eta) {
  //     return { label: "Pending", className: "status-pending" };
  //   }

  //   const now = new Date();
  //   const startDate = new Date(equipmentInfo.start_eta);
  //   const finishDate = new Date(equipmentInfo.finish_eta);

  //   if (now < startDate) {
  //     return { label: "Pending", className: "status-pending" };
  //   } else if (now >= startDate && now <= finishDate) {
  //     return { label: "In Progress", className: "status-progress" };
  //   } else {
  //     return { label: "Completed", className: "status-completed" };
  //   }
  // };

  const handleGoBack = () => {
    navigate(-1);
  };

  const hasDrawing = drawingsCount > 0;
  const getDrawingLabel = (file) =>
    file?.display_name || file?.filename || "Unnamed file";

  const handleDrawingUploaded = (result) => {
    toast.success("Drawing uploaded successfully", {
      containerId: "equipment-details-toast",
    });
    // Lightweight: bump the count locally so "View Drawing" appears immediately,
    // without re-running the heavy process fetch. The picker lazy-loads fresh.
    const uploadedCount = Array.isArray(result?.uploaded_files)
      ? result.uploaded_files.length
      : 1;
    setDrawingsCount((prev) => prev + uploadedCount);
  };

  // Lazy-load the drawing list, then open it directly (1) or via the picker (2+).
  const handleViewDrawing = async () => {
    if (!hasDrawing || !equipment_id) {
      toast.warning("No drawing uploaded for this equipment", {
        containerId: "equipment-details-toast",
      });
      return;
    }

    setDrawingsListOpen(true);
    setDrawingsListLoading(true);
    setDrawingsList([]);
    try {
      const res = await axiosInstance.get(
        `${API_URL}${equipmentDrawingsListPath(equipment_id)}`,
      );
      const files = res?.data?.files || [];
      setDrawingsList(files);
      setDrawingsCount(files.length);
      if (files.length === 1) {
        setDrawingsListOpen(false);
        setPreviewIndex(0);
      } else if (files.length === 0) {
        setDrawingsListOpen(false);
        toast.warning("No drawing uploaded for this equipment", {
          containerId: "equipment-details-toast",
        });
      }
    } catch (error) {
      setDrawingsListOpen(false);
      toast.error("Unable to load drawings", {
        containerId: "equipment-details-toast",
      });
    } finally {
      setDrawingsListLoading(false);
    }
  };

  // Keep the local drawing count in sync with the server count whenever the
  // equipment payload (re)loads. Local optimistic bumps after upload are not
  // clobbered because equipment_info only changes on a full reload.
  useEffect(() => {
    setDrawingsCount(equipmentInfo?.drawings_count || 0);
  }, [equipmentInfo?.drawings_count]);

  // ---- Delete drawing (Admin or Planning Field Technician) ----
  const requestDeleteDrawing = (file) => setDrawingDeleteTarget(file);
  const cancelDeleteDrawing = () => {
    if (drawingDeleteBusy) return;
    setDrawingDeleteTarget(null);
  };

  const confirmDeleteDrawing = async () => {
    const target = drawingDeleteTarget;
    if (!target?.file_id) return;
    setDrawingDeleteBusy(true);
    try {
      await axiosInstance.delete(
        `${API_URL}${equipmentDrawingDeletePath(target.file_id)}`,
      );

      const next = drawingsList.filter((f) => f.file_id !== target.file_id);
      setDrawingsList(next);
      setDrawingsCount(next.length);
      // Advance the preview within the remaining set, or close if empty.
      setPreviewIndex((i) => {
        if (i === null) return i;
        if (next.length === 0) return null;
        return Math.min(i, next.length - 1);
      });

      toast.success("Drawing deleted", {
        containerId: "equipment-details-toast",
      });
      setDrawingDeleteTarget(null);
    } catch (error) {
      const status = error?.response?.status;
      toast.error(
        status === 403
          ? "You do not have permission to delete drawings"
          : "Unable to delete drawing",
        { containerId: "equipment-details-toast" },
      );
    } finally {
      setDrawingDeleteBusy(false);
    }
  };

  // ---- Rename drawing (friendly display name) ----
  const requestRenameDrawing = (file) => {
    setDrawingRenameTarget(file);
    setDrawingRenameValue(getDrawingLabel(file));
  };
  const cancelRenameDrawing = () => {
    if (drawingRenameBusy) return;
    setDrawingRenameTarget(null);
  };

  const confirmRenameDrawing = async () => {
    const target = drawingRenameTarget;
    const name = drawingRenameValue.trim();
    if (!target?.file_id || !name) return;
    setDrawingRenameBusy(true);
    try {
      await axiosInstance.patch(
        `${API_URL}${equipmentDrawingRenamePath(target.file_id)}`,
        { display_name: name },
      );
      setDrawingsList((prev) =>
        prev.map((f) =>
          f.file_id === target.file_id ? { ...f, display_name: name } : f,
        ),
      );
      toast.success("Drawing renamed", {
        containerId: "equipment-details-toast",
      });
      setDrawingRenameTarget(null);
    } catch (error) {
      const status = error?.response?.status;
      toast.error(
        status === 403
          ? "You do not have permission to rename drawings"
          : "Unable to rename drawing",
        { containerId: "equipment-details-toast" },
      );
    } finally {
      setDrawingRenameBusy(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
  };

  const handleCardClick = (processId) => {
    const normalizedProcessId = String(processId || "");
    if (!normalizedProcessId) return;

    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Set the highlighted process ID
    setHighlightedProcessId(normalizedProcessId);

    // Auto-scroll to the specific highlighted row
    scrollTimeoutRef.current = setTimeout(() => {
      // Find the specific row with the highlighted process
      const highlightedRow = document.querySelector(
        `tr[data-process-id="${normalizedProcessId}"]`,
      );
      if (highlightedRow) {
        // Scroll the specific row into view
        highlightedRow.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      } else {
        // Fallback: scroll to table if specific row not found
        const tableElement = document.querySelector(".execution-table");
        if (tableElement) {
          tableElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
    }, 100);

    // Remove highlight after ~3 seconds
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedProcessId(null);
      highlightTimeoutRef.current = null;
    }, 2800);
  };

  const handleExternalCardClick = (equipmentId, processName) => {
    if (equipmentId) {
      // Navigate to the equipment details page
      navigate(`/equipment-details/${equipmentId}`);
    }
  };

  const handleDependencyNavigate = (targetEquipmentId, targetProcessId) => {
    if (!targetEquipmentId) return;
    setPendingDependencyRedirect({
      targetEquipmentId,
      targetProcessId: targetProcessId || null,
    });
    setDependencyRedirectOpen(true);
  };

  const confirmDependencyRedirect = () => {
    if (!pendingDependencyRedirect?.targetEquipmentId) {
      setDependencyRedirectOpen(false);
      setPendingDependencyRedirect(null);
      return;
    }

    const query = pendingDependencyRedirect.targetProcessId
      ? `?process_id=${encodeURIComponent(
          pendingDependencyRedirect.targetProcessId,
        )}`
      : "";

    navigate(
      `/equipment-details/${pendingDependencyRedirect.targetEquipmentId}${query}`,
    );
    setDependencyRedirectOpen(false);
    setPendingDependencyRedirect(null);
  };

  const cancelDependencyRedirect = () => {
    setDependencyRedirectOpen(false);
    setPendingDependencyRedirect(null);
  };

  // Get unique process steps count
  const getTotalStepsCount = () => {
    return processSteps.length;
  };

  const equipmentIdentityTitle = useMemo(() => {
    if (!equipmentInfo) return "Equipment";

    const group = String(equipmentInfo.equipment_group || "").trim();
    const type = String(equipmentInfo.equipment_type || "").trim();

    if (group && type && group.toLowerCase() !== type.toLowerCase()) {
      return `${group} (${type})`;
    }
    return group || type || "Equipment";
  }, [equipmentInfo]);

  const handleHoldChanged = useCallback(
    async ({ action }) => {
      toast.success(
        action === "hold"
          ? "Equipment placed on hold"
          : "Equipment hold released",
      );
      await fetchEquipmentDetails({ force: true, silent: true });
    },
    [fetchEquipmentDetails],
  );

  const heldByName =
    equipmentInfo?.held_by?.name || equipmentInfo?.held_by_name || "Unknown user";

  useEffect(() => {
    if (!incomingProcessId || !processSteps.length) return;
    if (hasAutoFocusedProcessRef.current) return;

    const hasMatchingProcess = processSteps.some(
      (step) => String(step?.process_id) === String(incomingProcessId),
    );
    if (!hasMatchingProcess) return;

    hasAutoFocusedProcessRef.current = true;
    handleCardClick(incomingProcessId);
  }, [incomingProcessId, processSteps]);

  return (
    <div
      className={`table-container equipment-details-container ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      {/* Header */}
      <div
        ref={stickyHeaderRef}
        className={`equipment-details-header ${
          isStickyHeaderElevated ? "is-elevated" : ""
        }`}
      >
        <div className="header-left">
          <button className="back-button" onClick={handleGoBack}>
            <IoArrowBack size={18} />
            {/* <span>Back</span> */}
          </button>
          {equipmentInfo && (
            <div className="header-title">
              <h2 className="equipment-header-inline">
                <span>{equipmentIdentityTitle}</span>
                <span className="equipment-header-separator">|</span>
                <span className="equipment-id-inline">
                  Equipment ID: {equipmentInfo.equipment_tag_number}
                </span>
              </h2>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="equipment-details-content">
        {loading ? (
          <div className="equipment-loading-message">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p style={{ marginTop: "10px" }}>Loading equipment details...</p>
          </div>
        ) : error ? (
          <div className="equipment-error-message">
            <strong>Error:</strong> {error}
            <button
              onClick={fetchEquipmentDetails}
              style={{
                marginLeft: "10px",
                padding: "5px 10px",
                cursor: "pointer",
              }}
            >
              Retry
            </button>
          </div>
        ) : equipmentInfo ? (
          <>
            <div className={`equipment-summary-layout ${equipmentInfo.is_on_hold ? "has-hold-card" : ""}`}>
            {/* Equipment Information Card */}
            <div className="equipment-info-card equipment-summary-card">
              <div className="equipment-summary-header">
                <div className="equipment-summary-title-group">
                  <span className="equipment-summary-heading-icon" aria-hidden="true"><InfoIcon /></span>
                  <h3 className="card-title mb-0">Equipment Information</h3>
                </div>
                <div className="equipment-summary-actions">
                  <EquipmentHoldControl
                    equipmentId={equipmentInfo.id || equipment_id}
                    equipmentNumber={equipmentInfo.equipment_tag_number}
                    isOnHold={Boolean(equipmentInfo.is_on_hold)}
                    holdReason={equipmentInfo.hold_reason || ""}
                    canManage={
                      equipmentInfo.can_manage_hold ?? roleCanManageHold
                    }
                    compact
                    onChanged={handleHoldChanged}
                    onRequestError={(requestError) => {
                      if (!requestError?.response) {
                        toast.error("Unable to refresh the equipment hold state");
                      }
                    }}
                  />
                  {(equipmentInfo.can_manage_hold ?? roleCanManageHold) && (
                    <button
                      type="button"
                      className="equipment-history-button"
                      onClick={openHoldHistory}
                    >
                      <HistoryIcon />
                      <span>Hold history</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="info-grid compact">
                <div className="info-item">
                  <label className="info-label">Tag Number</label>
                  <p className="info-value tag-number">
                    {equipmentInfo.equipment_tag_number || "N/A"}
                  </p>
                </div>
                <div className="info-item">
                  <label className="info-label">Equipment Type</label>
                  <p className="info-value">
                    {equipmentInfo.equipment_type || "N/A"}
                  </p>
                </div>
                <div className="info-item">
                  <label className="info-label">Equipment Group</label>
                  <span className="info-value">
                    {equipmentInfo.equipment_group || "-"}
                  </span>
                </div>
                <div className="info-item">
                  <label className="info-label">Total Duration</label>
                  <p className="info-value">
                    {equipmentInfo.total_duration_eta
                      ? `${equipmentInfo.total_duration_eta} hrs`
                      : "N/A"}
                  </p>
                </div>
                <div className="info-item">
                  <label className="info-label">Total Steps</label>
                  <p className="info-value">{getTotalStepsCount()}</p>
                </div>
                <div className="info-item">
                  <label className="info-label">Start ETA</label>
                  <p className="info-value">
                    {formatDate(equipmentInfo.start_eta)}
                  </p>
                </div>
                <div className="info-item">
                  <label className="info-label">Finish ETA</label>
                  <p className="info-value">
                    {formatDate(equipmentInfo.finish_eta)}
                  </p>
                </div>
                <div className="info-item">
                  <label className="info-label">Drawing</label>
                  <div className="equipment-drawing-inline">
                    {hasDrawing ? (
                      <button
                        type="button"
                        className="equipment-drawing-link"
                        onClick={handleViewDrawing}
                        disabled={drawingsListLoading}
                      >
                        {drawingsListLoading ? (
                          <CircularProgress size={13} />
                        ) : (
                          <LuFileText size={13} />
                        )}
                        <span>
                          View Drawing
                          {drawingsCount > 1 ? ` (${drawingsCount})` : ""}
                        </span>
                      </button>
                    ) : (
                      <span className="equipment-drawing-empty">
                        No drawing uploaded
                      </span>
                    )}
                    {canManageDrawings && (
                      <Tooltip title="Upload Drawing">
                        <button
                          type="button"
                          className="equipment-drawing-action-btn"
                          onClick={() => setDrawingUploadOpen(true)}
                        >
                          <CloudUploadIcon style={{ fontSize: "15px" }} />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </div>
                <div className="info-item">
                  <label className="info-label">Project Name</label>
                  <p className="info-value">{equipmentInfo.project_name || "N/A"}</p>
                </div>
              </div>
            </div>

            {equipmentInfo.is_on_hold && (
              <section className="equipment-hold-card" role="status">
                <div className="equipment-hold-card-header">
                  <div className="equipment-hold-card-heading">
                    <h3>Equipment on hold</h3>
                  </div>
                  <span className="equipment-hold-card-badge">On Hold</span>
                </div>
                <div className="equipment-hold-card-body">
                  <div>
                    <span className="equipment-hold-card-label">Reason</span>
                    <p className="equipment-hold-card-reason">
                      {equipmentInfo.hold_reason || "No hold reason was provided."}
                    </p>
                  </div>
                  <div className="equipment-hold-card-meta">
                    <div className="equipment-hold-card-meta-item">
                      <span className="equipment-hold-card-label">Held by</span>
                      <strong>{heldByName}</strong>
                    </div>
                    <div className="equipment-hold-card-meta-item">
                      <span className="equipment-hold-card-label">Held at</span>
                      <strong>{formatHoldDateTime(equipmentInfo.held_at)}</strong>
                    </div>
                  </div>
                  <div className="equipment-hold-duration">
                    <span className="equipment-hold-card-label">Duration on hold</span>
                    <strong>{formatHoldDuration(equipmentInfo.held_at)}</strong>
                  </div>
                  {/*
                  <small>
                    Held by {heldByName}
                    {equipmentInfo.held_at
                      ? ` • ${formatDate(equipmentInfo.held_at)}`
                      : ""}
                  </small>
                  */}
                </div>
                <div className="equipment-hold-card-actions">
                  {(equipmentInfo.can_manage_hold ?? roleCanManageHold) && (
                    <div className="equipment-hold-card-release">
                      <EquipmentHoldControl
                        equipmentId={equipmentInfo.id || equipment_id}
                        equipmentNumber={equipmentInfo.equipment_tag_number}
                        isOnHold
                        holdReason={equipmentInfo.hold_reason || ""}
                        canManage
                        compact
                        onChanged={handleHoldChanged}
                      />
                    </div>
                  )}
                  <button type="button" className="equipment-hold-card-history" onClick={openHoldHistory}>
                    View Hold History
                  </button>
                </div>
              </section>
            )}
            </div>

            {/* Process Flow Visualization Card */}
            <div className="equipment-info-card mt-3">
              <div
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", width: "100%" }}
                onClick={() => setIsFlowchartExpanded(!isFlowchartExpanded)}
              >
                <h3 className="card-title mb-0">Process Flow Visualization</h3>
                <Tooltip
                  title={isFlowchartExpanded ? "Collapse" : "Expand"}
                  arrow
                  placement="left"
                >
                  <div className="border px-2 py-2 rounded-3">
                    <FaChevronDown
                      style={{
                        transform: isFlowchartExpanded
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.3s ease",
                        fontSize: "14px",
                        display: "block",
                      }}
                    />
                  </div>
                </Tooltip>
              </div>

              {isFlowchartExpanded && (
                <ProcessFlowchartVisualization
                  processSteps={processSteps}
                  dependencyMap={dependencyMap}
                  onCardClick={handleCardClick}
                  selectedProcessId={highlightedProcessId}
                  onExternalCardClick={handleExternalCardClick}
                />
              )}
            </div>
          </>
        ) : (
          <div className="equipment-no-data">
            <p>No equipment data found.</p>
          </div>
        )}
      </div>

      <div className="equipment-info-card mt-3 equipment-execution-card-shell">
        <EquipmentExecutionTable
          processSteps={tableProcessSteps}
          equipmentTimezone={
            equipmentInfo?.project_tz ||
            equipmentInfo?.project_timezone ||
            equipmentInfo?.timezone ||
            equipmentInfo?.tz ||
            ""
          }
          addAdminNoteToStep={addAdminNoteToStep}
          onProcessBypassed={applyBypassToProcess}
          dependencyMap={tableDependencyMap}
          equipmentId={equipment_id}
          isEquipmentOnHold={Boolean(equipmentInfo?.is_on_hold)}
          equipmentHoldReason={equipmentInfo?.hold_reason || ""}
          canEditProcessTime={Boolean(equipmentInfo?.can_edit_process_time)}
          refreshEquipmentDetails={fetchEquipmentDetails}
          selectedProcessId={highlightedProcessId}
          onDependencyNavigate={handleDependencyNavigate}
          realtimeProcessUpdate={realtimeProcessUpdate}
          realtimeUpdateSignal={realtimeUpdateSignal}
          tableLoading={tableLoading}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onClearSearch={clearSearch}
        />
      </div>

      <Dialog
        open={dependencyRedirectOpen}
        onClose={cancelDependencyRedirect}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Redirect to Dependency
        </DialogTitle>
        <DialogContent dividers>
          Are you sure you want to redirect to the dependency's equipment details?
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={cancelDependencyRedirect}
            variant="outlined"
            sx={{
              borderColor: "#d3a3a3",
              color: "#7f1d1d",
              fontWeight: 600,
              "&:hover": {
                borderColor: "#b91c1c",
                backgroundColor: "#fff5f5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDependencyRedirect}
            variant="contained"
            sx={{
              backgroundColor: "#b71c1c",
              "&:hover": { backgroundColor: "#9f1919" },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <EquipmentDrawingUploadModal
        isOpen={drawingUploadOpen}
        onClose={() => setDrawingUploadOpen(false)}
        equipment={equipmentInfo}
        onUploaded={handleDrawingUploaded}
      />

      <Dialog
        open={drawingsListOpen}
        onClose={() => setDrawingsListOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: "jecon-dialog" }}
      >
        <DialogTitle>All Drawings</DialogTitle>
        <DialogContent dividers>
          {drawingsListLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "24px",
              }}
            >
              <CircularProgress size={26} />
            </div>
          ) : drawingsList.length > 0 ? (
            <div className="equipment-drawings-picker">
              {drawingsList.map((file, idx) => {
                const meta = formatDrawingMeta(file);
                return (
                  <div key={file.file_id} className="equipment-drawings-picker-row">
                    <button
                      type="button"
                      className="equipment-drawings-picker-item"
                      title={getDrawingLabel(file)}
                      onClick={() => {
                        setDrawingsListOpen(false);
                        setPreviewIndex(idx);
                      }}
                    >
                      <LuFileText size={14} />
                      <span className="equipment-drawings-picker-info">
                        <span className="equipment-drawings-picker-name">
                          {getDrawingLabel(file)}
                        </span>
                        {meta && (
                          <span className="equipment-drawings-picker-meta">
                            {meta}
                          </span>
                        )}
                      </span>
                    </button>
                    {canManageDrawings && (
                      <Tooltip title="Rename drawing">
                        <button
                          type="button"
                          className="equipment-drawings-picker-edit"
                          onClick={() => requestRenameDrawing(file)}
                          aria-label="Rename drawing"
                        >
                          <EditIcon style={{ fontSize: "17px" }} />
                        </button>
                      </Tooltip>
                    )}
                    {canManageDrawings && (
                      <Tooltip title="Delete drawing">
                        <button
                          type="button"
                          className="equipment-drawings-picker-delete"
                          onClick={() => requestDeleteDrawing(file)}
                          aria-label="Delete drawing"
                        >
                          <DeleteIcon style={{ fontSize: "18px" }} />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ color: "#667085", margin: 0 }}>
              No drawings available.
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDrawingsListOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <DrawingPreviewModal
        open={previewIndex !== null}
        files={drawingsList}
        index={previewIndex ?? 0}
        onIndexChange={setPreviewIndex}
        onClose={() => setPreviewIndex(null)}
        canDelete={canManageDrawings}
        onDelete={requestDeleteDrawing}
      />

      <Dialog
        open={Boolean(drawingDeleteTarget)}
        onClose={cancelDeleteDrawing}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: "jecon-dialog" }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete drawing?</DialogTitle>
        <DialogContent dividers>
          This will permanently delete{" "}
          <strong>{getDrawingLabel(drawingDeleteTarget)}</strong>. This can't be
          undone.
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={cancelDeleteDrawing} disabled={drawingDeleteBusy}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteDrawing}
            variant="contained"
            color="error"
            disabled={drawingDeleteBusy}
            startIcon={
              drawingDeleteBusy ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {drawingDeleteBusy ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(drawingRenameTarget)}
        onClose={cancelRenameDrawing}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: "jecon-dialog" }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Rename drawing</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Drawing name"
            value={drawingRenameValue}
            onChange={(e) => setDrawingRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") confirmRenameDrawing();
            }}
            inputProps={{ maxLength: 255 }}
            disabled={drawingRenameBusy}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={cancelRenameDrawing} disabled={drawingRenameBusy}>
            Cancel
          </Button>
          <Button
            onClick={confirmRenameDrawing}
            variant="contained"
            disabled={drawingRenameBusy || !drawingRenameValue.trim()}
            startIcon={
              drawingRenameBusy ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            sx={{
              backgroundColor: "#b71c1c",
              "&:hover": { backgroundColor: "#9f1919" },
            }}
          >
            {drawingRenameBusy ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={holdHistoryOpen}
        onClose={() => setHoldHistoryOpen(false)}
        PaperProps={{ className: "equipment-hold-history-drawer" }}
      >
        <div className="equipment-history-header">
          <div>
            <h3>Hold history</h3>
            <p>{equipmentInfo?.equipment_tag_number || "Equipment"}</p>
          </div>
          <IconButton
            aria-label="Close hold history"
            onClick={() => setHoldHistoryOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <div className="equipment-history-body">
          {holdHistoryLoading ? (
            <div className="equipment-history-state">
              <CircularProgress size={24} />
              <span>Loading hold history...</span>
            </div>
          ) : holdHistoryError ? (
            <div className="equipment-history-error">
              <span>{holdHistoryError}</span>
              <button type="button" onClick={openHoldHistory}>Try again</button>
            </div>
          ) : holdHistory.length === 0 ? (
            <div className="equipment-history-empty">
              <HistoryIcon />
              <strong>No hold history yet</strong>
              <span>Hold and release events will appear here.</span>
            </div>
          ) : (
            <div className="equipment-history-timeline">
              {holdHistory.map((event) => {
                const isHold = String(event.action).toLowerCase() === "hold";
                return (
                  <article
                    className={`equipment-history-event ${isHold ? "is-hold" : "is-release"}`}
                    key={event.id}
                  >
                    <div className="equipment-history-marker">
                      {isHold ? <HoldIcon /> : <ReleaseIcon />}
                    </div>
                    <div className="equipment-history-event-content">
                      <div className="equipment-history-event-heading">
                        <strong>{isHold ? "Put on hold" : "Hold released"}</strong>
                        <time>{formatHoldDateTime(event.done_at)}</time>
                      </div>
                      {isHold && (
                        <p>{event.reason || "No reason was provided."}</p>
                      )}
                      <span className="equipment-history-user">
                        {event.performed_by_name || "Unknown user"}
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </Drawer>

      <ToastContainer
        containerId="equipment-details-toast"
        position="top-right"
        autoClose={4000}
        theme="light"
      />
    </div>
  );
};

export default EquipmentDetails;
